import React, { useCallback, memo } from 'react';
import { Paper, Box, Typography, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useExternalErrors } from '@alycecom/hooks';

import StyledSubmitButton from '../../../../../components/Shared/StyledSubmitButton';
import Avatar from '../../../../../components/Avatar/Avatar';
import { getErrors, getIsFormLoading } from '../../../store/thanks/thanks.selectors';
import { TThankYouForm, validationSchema } from '../../../store/thanks/thanks.schemas';
import { getSenderState } from '../../../../../store/users/users.selectors';

const useStyles = makeStyles(({ palette, spacing }) => ({
  paper: {
    backgroundColor: palette.common.white,
  },
  title: {
    fontSize: 24,
    lineHeight: 1.33,
    textAlign: 'center',
    marginBottom: spacing(2),
    wordBreak: 'break-word',
  },
  avatar: {
    position: 'absolute',
    width: 104,
    height: 104,
    objectFit: 'contain',
    top: -52,
    left: 'calc(50% - 52px)',
  },
  textareaLabel: {
    wordBreak: 'break-word',
    paddingRight: spacing(4),
  },
}));

export interface IThankYouFormProps {
  onSubmit: (message: string) => void;
}

const ThankYouForm = ({ onSubmit }: IThankYouFormProps) => {
  const classes = useStyles();

  const sender = useSelector(getSenderState);
  const isLoading = useSelector(getIsFormLoading);
  const externalErrors = useSelector(getErrors);

  const senderLogo = sender?.image || '';
  const senderName = sender?.fullName || '';
  const fieldLabel = `What would you like to say to ${senderName}?`;

  const handleSubmitForm = useCallback(
    data => {
      onSubmit(data.message);
    },
    [onSubmit],
  );

  const { errors, handleSubmit, setError, register } = useForm<TThankYouForm>({
    mode: 'all',
    defaultValues: {
      message: '',
    },
    resolver: yupResolver(validationSchema),
  });

  useExternalErrors<TThankYouForm>(setError, externalErrors);

  return (
    <form onSubmit={handleSubmit(handleSubmitForm)}>
      <Box mt={[10, 15]} mb={[4, 2]} maxWidth={500}>
        <Paper elevation={1} className={classes.paper}>
          <Box minWidth={[320, 500]} position="relative" px={[2, 4]} pt={8} pb={4}>
            <Avatar className={classes.avatar} src={senderLogo} />
            <Typography className={classes.title} data-testid="thankYouMessageHeader">
              Send a thank you note to {senderName}
            </Typography>
            <TextField
              data-testid="thankYouMessageTextArea"
              name="message"
              label={fieldLabel}
              placeholder={fieldLabel}
              fullWidth
              multiline
              rows={5}
              variant="outlined"
              inputRef={register}
              InputLabelProps={{ classes: { root: classes.textareaLabel } }}
              disabled={isLoading}
              error={!!errors.message}
              helperText={errors.message && errors.message.message}
            />
            <Box width={1} display="flex" justifyContent="flex-end" mt={2}>
              <StyledSubmitButton
                type="submit"
                width={134}
                variant="outlined"
                disabled={isLoading}
                data-testid="thankYouSubmitButton"
              >
                Submit
              </StyledSubmitButton>
            </Box>
          </Box>
        </Paper>
      </Box>
    </form>
  );
};

export default memo(ThankYouForm);
